var __assign = (this && this.__assign) || Object.assign || function(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
            t[p] = s[p];
    }
    return t;
};
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { timer as observableTimer } from 'rxjs';
import { debounce, distinctUntilChanged, withLatestFrom, filter, map } from 'rxjs/operators';
import { Actions, Effect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as actions from '../actions/storage-sync.actions';
import { storageSyncSelector } from '../reducers/index';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
var StorageSyncEffects = /** @class */ (function () {
    function StorageSyncEffects(actions$, store, window, location, router) {
        var _this = this;
        this.actions$ = actions$;
        this.store = store;
        this.window = window;
        this.location = location;
        this.router = router;
        /**
         * Effect to save the state to local storage.
         *
         * First checks to make sure the app is in a hydrated state to prevent
         * writing an empty object to storage.
         *
         * Then saves each key in the state to storage.
         */
        this.saveStateToStorage = this.actions$.ofType(actions.REQUEST_SAVE_TO_STORAGE).pipe(withLatestFrom(this.store), filter(function (_a) {
            var action = _a[0], state = _a[1];
            return state.storageSync.rehydrated === true;
        }), map(function (_a) {
            var action = _a[0];
            var payload = action.payload;
            Object.getOwnPropertyNames(payload).map(function (key) {
                var stateSlice = payload[key];
                if (typeof stateSlice !== 'undefined') {
                    _this.window.localStorage.setItem(key, JSON.stringify(payload[key]));
                }
            });
        }), map(function (res) {
            return new actions.ResponseSaveToStorage();
        }));
        /**
         * Rehydrates the state from storage.
         */
        this.nativeStorageSync = this.actions$.ofType(actions.REQUEST_REHYDRATE).pipe(map(function (action) {
            var rehydratedState = _this.rehydrateApplicationState();
            return { type: actions.RESPONSE_REHYDRATED, payload: rehydratedState };
        }));
        this.initNativeStorageSync = this.actions$
            .ofType('@ngrx/effects/init')
            .pipe(map(function (action) { return new actions.RequestRehydrate(); }));
        // We use this selector to decide when to write changes, and what to write
        this.store
            .select(storageSyncSelector)
            .pipe(distinctUntilChanged(), debounce(function () { return observableTimer(2000); }))
            .subscribe(function (stateChange) {
            _this.store.dispatch(new actions.RequestSaveToStorage(stateChange));
        });
    }
    /**
     * Utility function to parse JSON that contains dates into an object that
     * contains Date objects.
     * @param jsonData A json string that contains timestamps
     */
    StorageSyncEffects.prototype.parseWithDates = function (jsonData) {
        return JSON.parse(jsonData, function (key, value) {
            var detectDate = /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/;
            if (typeof value === 'string' && detectDate.test(value)) {
                return new Date(value);
            }
            if (key === 'loading') {
                return false;
            }
            return value;
        });
    };
    /**
     * Reloads the application state from storage.
     */
    StorageSyncEffects.prototype.rehydrateApplicationState = function () {
        var _this = this;
        var keys = Object.keys(this.window.localStorage);
        var values = keys.map(function (key) { return _this.window.localStorage.getItem(key); });
        return keys.reduce(function (acc, curr, idx) {
            var _a;
            var stateSlice = values[idx];
            // Forgive me:
            // I tried many many ways to get these off the Angular router
            // but they just weren't being picked up. Plus, we want to make
            // sure they are picked up immediately to avoid race conditions
            // with routing based on auth etc.
            var path = _this.location.path();
            var params = _this.parseUrlQueryParams(path);
            if (stateSlice) {
                var parsed = void 0;
                try {
                    parsed = _this.parseWithDates(stateSlice);
                }
                catch (parseErr) {
                    return acc;
                }
                var rehydrated = Object.assign({}, acc, (_a = {}, _a[curr] = parsed, _a));
                if (params && params.userId && params.accessToken) {
                    // Cleanup the query params which are no longer needed
                    _this.router.navigate(['.']);
                    rehydrated = __assign({}, rehydrated, { auth: __assign({}, (rehydrated.auth || {}), { auth: {
                                id: params.accessToken,
                                userId: params.userId,
                                created: new Date(0),
                                dirty: true,
                                ttl: 0
                            } }) });
                }
                return rehydrated;
            }
            return acc;
        }, {});
    };
    StorageSyncEffects.prototype.parseUrlQueryParams = function (urlPath) {
        if (urlPath.indexOf('?') >= 0) {
            var query = urlPath.slice(urlPath.indexOf('?') + 1);
            var queryParams = query.split('&').map(function (param) { return param.split('='); });
            return queryParams.reduce(function (prev, next) {
                var _a;
                var mapped = (_a = {},
                    _a[next[0]] = next[1],
                    _a);
                return __assign({}, prev, mapped);
            }, {});
        }
        return {};
    };
    __decorate([
        Effect(),
        __metadata("design:type", Object)
    ], StorageSyncEffects.prototype, "saveStateToStorage", void 0);
    __decorate([
        Effect(),
        __metadata("design:type", Object)
    ], StorageSyncEffects.prototype, "nativeStorageSync", void 0);
    __decorate([
        Effect(),
        __metadata("design:type", Object)
    ], StorageSyncEffects.prototype, "initNativeStorageSync", void 0);
    return StorageSyncEffects;
}());
export { StorageSyncEffects };
